import React, { useMemo } from 'react';
import { PLAN_TABLE_GROUPS, PLAN_TABLE_TEAM_ITEM, planItemOn, type PlanTableItem } from '../../constants/planTableItems';
import { usePlanGateRows, type PlanGateRow } from '../../hooks/usePlanGateRows';
import { defaultPlanPermission, PLUS_CANONICAL_PLAN_KEY } from '../../services/planPermissionsService';
import { formatSlideStorageMb } from '../../services/slideImageStorageService';
import type { AccountPassCategory } from '../../utils/subscriptionPassStorage';
import './PassPlanComparisonTable.css';

/**
 * 이용권 구입 섹션 아래 「프리 vs 이용권」 비교표.
 * - O/X 는 관리자 「이용권별 권한 관리」의 FREE 열 → FREE 이용권, PLUS 열 → PLUS 이용권 값을 그대로 보여 준다.
 *   못 읽으면 안내용 기본값. 항목 ↔ 권한 키 묶음은 src/constants/planTableItems.ts (묶인 키가 모두 켜져야 ○).
 * - PLUS 계열(베타·이벤트·개별·그룹)은 권한이 모두 같다(usePlanGateRows 가 하나로 합침).
 * - 관리자 저장 시 notifyPlanPermissionsChanged → usePlanGateRows 가 다시 읽어 바로 갱신된다.
 * - 값 행: 클래스 등록 상한(max_classes)·월 AI 크레딧(ai_monthly_credits)은 권한표를 읽고, 못 읽으면 기본값.
 *   저장용량은 storage_limit_bytes(서버 업로드 한도와 같은 값), 못 읽으면 기본값(프리 10MB / 이용권 100MB).
 */

type Mark = 'ok' | 'no';
type Cell = { mark: Mark } | { value: string };
type SubRow = { label: string; free: Cell; pass: Cell };
type Group = { title: string; rows: SubRow[] };

const OK: Cell = { mark: 'ok' };
const NO: Cell = { mark: 'no' };

/** 권한표 행 → 항목 칸(행이 없거나 AI 열을 못 읽으면 안내용 기본값) */
function itemCell(item: PlanTableItem, row: PlanGateRow | null, isFree: boolean): Cell {
  const fallback = isFree ? item.tableDefault.free : item.tableDefault.pass;
  const state = row ? planItemOn(item, { features: row.features, ai: row.ai, aiFeatures: row.aiFeatures }, isFree) : null;
  return (state ?? fallback) ? OK : NO;
}
const itemRow = (item: PlanTableItem, freeRow: PlanGateRow | null, passRow: PlanGateRow | null): SubRow => ({
  label: item.label,
  free: itemCell(item, freeRow, true),
  pass: itemCell(item, passRow, false),
});

const MARK_LABEL: Record<Mark, string> = { ok: '사용 가능', no: '사용 불가' };

function CompareMark({ mark }: { mark: Mark }) {
  return (
    <span className={`cp-pass-compare-badge is-${mark}`} role="img" aria-label={MARK_LABEL[mark]}>
      <svg viewBox="0 0 24 24" aria-hidden>
        {mark === 'ok' ? (
          <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="3" fill="none" />
        ) : (
          <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
        )}
      </svg>
    </span>
  );
}

function CellView({ cell }: { cell: Cell }) {
  return 'mark' in cell ? <CompareMark mark={cell.mark} /> : <span className="cp-pass-compare-value">{cell.value}</span>;
}

type Props = {
  /** 이전 호환용(무시해도 됨). PLUS 열은 항상 PLUS 공통 권한(관리자 「PLUS 이용권」 열)을 보여 준다. */
  passPlanKey?: AccountPassCategory;
};

export default function PassPlanComparisonTable(_props: Props) {
  const rows = usePlanGateRows();

  const { settings, groups } = useMemo(() => {
    const freeDefault = defaultPlanPermission('free');
    const passDefault = defaultPlanPermission(PLUS_CANONICAL_PLAN_KEY);
    const freeLive = rows?.free ?? null;
    const passLive = rows?.[PLUS_CANONICAL_PLAN_KEY] ?? null;

    const credits = (n: number | null | undefined, fallback: number) => {
      const v = typeof n === 'number' ? n : fallback;
      return v > 0 ? `${v.toLocaleString('ko-KR')} 크레딧` : '없음';
    };
    const storage = (n: number | null | undefined, fallback: number | null) =>
      formatSlideStorageMb(typeof n === 'number' && n > 0 ? n : fallback ?? 0);

    const settings: SubRow[] = [
      {
        label: '클래스 등록 상한',
        free: { value: `${freeLive?.maxClasses ?? freeDefault.max_classes}개` },
        pass: { value: `${passLive?.maxClasses ?? passDefault.max_classes}개` },
      },
      itemRow(PLAN_TABLE_TEAM_ITEM, freeLive, passLive),
      {
        label: '저장용량',
        free: { value: storage(freeLive?.storageLimitBytes, freeDefault.storage_limit_bytes) },
        pass: { value: storage(passLive?.storageLimitBytes, passDefault.storage_limit_bytes) },
      },
      {
        label: '월 AI 크레딧',
        free: { value: credits(freeLive?.aiMonthlyCredits, freeDefault.ai_monthly_credits) },
        pass: { value: credits(passLive?.aiMonthlyCredits, passDefault.ai_monthly_credits) },
      },
    ];

    const groups: Group[] = PLAN_TABLE_GROUPS.map((g) => ({
      title: g.title,
      rows: g.items.map((item) => itemRow(item, freeLive, passLive)),
    }));
    return { settings, groups };
  }, [rows]);

  return (
    <section className="cp-sub-pass-section-block cp-pass-compare" aria-labelledby="cp-pass-compare-title">
      <h2 id="cp-pass-compare-title" className="cp-pass-compare-title">
        FREE · PLUS 이용권 기능 비교
      </h2>
      <div className="cp-pass-compare-scroll">
        <table className="cp-pass-compare-table">
          <colgroup>
            <col className="cp-pass-compare-col-category" />
            <col />
            <col className="cp-pass-compare-col-plan" />
            <col className="cp-pass-compare-col-plan" />
          </colgroup>
          <thead>
            <tr>
              <th scope="col">구분</th>
              <th scope="col">기능</th>
              <th scope="col" className="cp-pass-compare-plan-head">
                FREE 이용권
              </th>
              <th scope="col" className="cp-pass-compare-plan-head">
                PLUS 이용권
              </th>
            </tr>
          </thead>
          <tbody className="cp-pass-compare-settings">
            {settings.map((r) => (
              <tr key={r.label}>
                <th scope="row" colSpan={2} className="cp-pass-compare-main">
                  {r.label}
                </th>
                <td>
                  <CellView cell={r.free} />
                </td>
                <td className="cp-pass-compare-pass">
                  <CellView cell={r.pass} />
                </td>
              </tr>
            ))}
          </tbody>
          {groups.map((g) => (
            <tbody key={g.title} className="cp-pass-compare-group">
              {g.rows.map((r, i) => (
                <tr key={r.label}>
                  {i === 0 ? (
                    <th scope="rowgroup" rowSpan={g.rows.length} className="cp-pass-compare-main cp-pass-compare-category">
                      {g.title}
                    </th>
                  ) : null}
                  <td className="cp-pass-compare-feature">{r.label}</td>
                  <td>
                    <CellView cell={r.free} />
                  </td>
                  <td className="cp-pass-compare-pass">
                    <CellView cell={r.pass} />
                  </td>
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
    </section>
  );
}
